class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.elcLockPage();
  }

  /* ELC 2026-10-27 (Zein): iPhone Safari ignores overflow:hidden, so deleting items in the drawer could
     scroll the page underneath back to the top of the collection. While the drawer is open the page is
     pinned where it was, and closing the drawer returns the customer to exactly that spot. */
  elcLockPage() {
    if (this._elcY != null) return;
    const y = window.scrollY || window.pageYOffset || 0;
    this._elcY = y;
    const b = document.body.style;
    b.position = 'fixed'; b.top = -y + 'px'; b.left = '0'; b.right = '0'; b.width = '100%';
  }

  elcUnlockPage() {
    if (this._elcY == null) return;
    const y = this._elcY;
    this._elcY = null;
    const b = document.body.style;
    b.position = ''; b.top = ''; b.left = ''; b.right = ''; b.width = '';
    try { window.scrollTo({ top: y, left: 0, behavior: 'instant' }); } catch (e) { window.scrollTo(0, y); }
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    this.elcUnlockPage();
    removeTrapFocus(this.activeElement);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /* ELC 2026-10-27: add the lighter's butane BEFORE the drawer paints, so the quick cart opens once,
     complete, instead of opening and then re-shuffling when the auto-gift lands a second later.
     Same sessionStorage rules as the auto-gift in snippets/cart-drawer.liquid (elgAGa_/elgAGd_). */
  elcPendingGifts(html) {
    try {
      const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-elg-autogift]');
      if (!el) return null;
      const fp = el.getAttribute('data-elg-fp') || '';
      if (el.getAttribute('data-elg-empty') === '1' || !fp) return null;
      const ids = (el.getAttribute('data-elg-autogift') || '').split(',').filter(Boolean);
      const pending = [];
      ids.forEach((id) => {
        let dis = null, add = null;
        try { dis = sessionStorage.getItem('elgAGd_' + id); add = sessionStorage.getItem('elgAGa_' + id); } catch (e) {}
        if (dis === fp) return;
        if (add === fp) { try { sessionStorage.setItem('elgAGd_' + id, fp); } catch (e) {} return; }
        pending.push(id);
      });
      return pending.length ? { ids: pending, fp } : null;
    } catch (e) { return null; }
  }

  renderContents(parsedState) {
    const gifts = parsedState && parsedState.sections && !parsedState.__elcGift
      ? this.elcPendingGifts(parsedState.sections['cart-drawer'] || '')
      : null;
    if (gifts) {
      /* keep the button's spinner going while the butane is added */
      const btns = Array.from(document.querySelectorAll('button.loading'));
      const hold = () => btns.forEach((b) => {
        b.classList.add('loading'); b.setAttribute('aria-disabled', 'true');
        const sp = b.querySelector('.loading__spinner'); if (sp) sp.classList.remove('hidden');
      });
      setTimeout(hold);
      const release = () => btns.forEach((b) => {
        b.classList.remove('loading'); b.removeAttribute('aria-disabled');
        const sp = b.querySelector('.loading__spinner'); if (sp) sp.classList.add('hidden');
      });
      gifts.ids.forEach((id) => { try { sessionStorage.setItem('elgAGa_' + id, gifts.fp); sessionStorage.removeItem('elgAGd_' + id); } catch (e) {} });
      const url = ((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js';
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: gifts.ids.map((id) => ({ id: parseInt(id, 10), quantity: 1 })),
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(r)))
        .then((res) => {
          const ok = res && res.sections && res.sections['cart-drawer'];
          this.renderContents(Object.assign({}, parsedState, { __elcGift: true }, ok ? { sections: res.sections } : {}));
        })
        .catch(() => this.renderContents(Object.assign({}, parsedState, { __elcGift: true })))
        .then(() => setTimeout(release, 0));
      return;
    }
    this._elcRenderSeq = (this._elcRenderSeq || 0) + 1;
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

/* ELC 2026-10-27 (Zein): "Continue shopping" inside the quick cart (including the emptied cart) just closes the
   cart, so the shopper stays on the page — and the spot — they were on. It used to leave the page: the empty-cart
   button links to the all-products page, and a script in layout/theme.liquid redirects it to the last brand
   collection (page 1). This listener sits on window in the capture phase, so it runs before that document-level
   redirect. The full /cart page keeps its redirect. */
window.addEventListener(
  'click',
  (event) => {
    const link = event.target.closest && event.target.closest('cart-drawer .drawer__inner-empty a.button, cart-drawer .cart-drawer__empty-content a.button');
    if (!link) return;
    event.preventDefault();
    event.stopPropagation();
    const drawer = link.closest('cart-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  },
  true
);

class CartDrawerItems extends CartItems {
  /* ELC 2026-10-27: Dawn re-downloads the drawer on every cart update. When the drawer was already
     re-rendered with newer data while that download was in flight (e.g. the butane just added),
     the late copy used to overwrite it with the cart as it was BEFORE the butane — the line
     vanished and the auto-gift took it as "customer removed it". Drop such stale copies. */
  onCartUpdate() {
    const drawer = document.querySelector('cart-drawer');
    const seq = drawer ? drawer._elcRenderSeq || 0 : 0;
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        if (drawer && (drawer._elcRenderSeq || 0) !== seq) return;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
